import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Printer, RotateCcw, Search } from "lucide-react";
import { PageShell, usePageTitle } from "./components/site/Layout";
import { buttons, container } from "./components/site/ui";
import { FaultList, SectionTitle } from "./components/report/ReportParts";
import { MANUAL_API_URL } from "./config";
import { formatDate, formatNumber } from "./lib/report";

const PARAMS = ["brand", "model", "variant", "year", "miles", "gearbox"];

function describeError(error) {
  if (error.code === "ECONNABORTED") {
    return "The server didn't respond in time. It may have been starting up, so please try again.";
  }
  if (!error.response) return "We couldn't reach Car Sense. Check your internet connection and try again.";
  return "We couldn't get the faults for this car. Please try again in a moment.";
}

export default function ManualResults() {
  const [searchParams] = useSearchParams();
  const query = Object.fromEntries(PARAMS.map((name) => [name, (searchParams.get(name) || "").trim()]));
  const complete = PARAMS.every((name) => query[name]);
  const queryString = new URLSearchParams(query).toString();

  const [state, setState] = useState({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(null);

  const vehicleName = `${query.year} ${query.brand} ${query.model}`.trim().toUpperCase();
  usePageTitle(complete ? `${vehicleName} common faults` : "Make and model search");

  useEffect(() => {
    if (!complete) {
      setState({ status: "error", missing: true });
      return;
    }

    // React runs effects twice in development; this stops a second (paid) API call.
    const requestKey = `${queryString}|${attempt}`;
    if (requestRef.current === requestKey) return;
    requestRef.current = requestKey;
    setState({ status: "loading" });

    axios
      .get(`${MANUAL_API_URL}/api/faults`, {
        params: Object.fromEntries(new URLSearchParams(queryString)),
        timeout: 60000,
      })
      .then(({ data }) => {
        if (requestRef.current === requestKey) {
          setState({ status: "ready", faults: Array.isArray(data?.faults) ? data.faults : [] });
        }
      })
      .catch((error) => {
        console.error("Error fetching faults:", error);
        if (requestRef.current === requestKey) setState({ status: "error", message: describeError(error) });
      });
  }, [complete, queryString, attempt]);

  if (state.status === "loading") {
    return (
      <PageShell>
        <div
          className={`${container} flex min-h-[60vh] flex-col items-center justify-center py-16 text-center`}
          role="status"
          aria-live="polite"
        >
          <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" aria-hidden="true" />
          <h1 className="mt-6 text-xl font-semibold text-slate-900">Looking up {vehicleName}</h1>
          <p className="mt-2 max-w-md text-slate-600">
            We're analysing the known faults for this car. This can take up to a minute.
          </p>
        </div>
      </PageShell>
    );
  }

  if (state.status === "error") {
    return (
      <PageShell>
        <div className={`${container} flex min-h-[60vh] items-center justify-center py-16`}>
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" aria-hidden="true" />
            <h1 className="mt-4 text-xl font-semibold text-slate-900">
              {state.missing ? "Some details are missing" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-slate-600">
              {state.missing ? "Enter the car's make, model, variant, year, mileage and gearbox to search." : state.message}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {!state.missing && (
                <button type="button" onClick={() => setAttempt((count) => count + 1)} className={buttons.primary}>
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  Try again
                </button>
              )}
              <Link to="/manual-search" className={state.missing ? buttons.primary : buttons.secondary}>
                New search
              </Link>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  const { faults } = state;

  return (
    <PageShell>
      <section className="border-b border-slate-200 bg-slate-50">
        <div className={`${container} py-8 sm:py-10`}>
          <p className="text-sm text-slate-500">Make and model search · {formatDate(new Date())}</p>
          <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{vehicleName}</h1>
              <p className="mt-1 text-slate-600">
                {[query.variant, query.gearbox, `${formatNumber(query.miles)} miles`].join(" · ")}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 print:hidden">
              <button type="button" onClick={() => window.print()} className={buttons.secondary}>
                <Printer className="h-4 w-4" aria-hidden="true" />
                Print or save as PDF
              </button>
              <Link to="/manual-search" className={buttons.primary}>
                <Search className="h-4 w-4" aria-hidden="true" />
                New search
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className={`${container} space-y-12 py-10 sm:py-12`}>
        <div className="flex flex-col gap-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 sm:flex-row sm:items-center sm:justify-between print:hidden">
          <p>
            <span className="font-semibold">MOT history isn't included.</span> It's tied to the registration, so run a
            registration check to see the car's MOT record and mileage.
          </p>
          <Link to="/#search" className={`${buttons.secondary} shrink-0`}>
            Check by registration
          </Link>
        </div>

        <section aria-labelledby="faults-heading">
          <SectionTitle id="faults-heading" title="Common faults" count={faults.length}>
            Known issues for this make, model and year. Repair costs are estimates at the higher end of UK prices.
          </SectionTitle>
          {faults.length ? (
            <div className="pt-6">
              <FaultList faults={faults} />
            </div>
          ) : (
            <p className="pt-6 text-slate-600">No faults came back for this car. Check the details and try again.</p>
          )}
        </section>

        <p className="border-t border-slate-200 pt-6 text-xs leading-5 text-slate-500">
          These faults are generated by AI from the make, model, year and mileage you entered, so treat them as guidance
          rather than a guarantee of the vehicle's condition. If you're unsure, have the car inspected by a qualified
          mechanic.
        </p>
      </div>
    </PageShell>
  );
}
